import { action, computed, makeObservable, observable } from 'mobx';

import { TREE_ROOT_NODE_ID, TreeNodeFile } from '@/entities/Tree';
import { TreeStore } from '@/stores/local/shared/TreeStore';

type Mark = 'include' | 'exclude';

export type NodeCheckboxState = {
  checked: boolean;
  indeterminate: boolean;
};

export type ServerSelectionContext = {
  documents: string[];
  folders: string[];
};

type DocumentsTreeSelectionStoreParams = {
  treeStore: TreeStore;
  isSelectAll?: boolean;
};

type PrivateFields = '_singleOnlyId';

export class DocumentsTreeSelectionStore {
  readonly marks = observable.map<string, Mark>();
  private readonly treeStore: TreeStore;
  private _singleOnlyId: string | null = null;

  constructor({ treeStore, isSelectAll }: DocumentsTreeSelectionStoreParams) {
    this.treeStore = treeStore;

    makeObservable<DocumentsTreeSelectionStore, PrivateFields>(this, {
      marks: observable,
      _singleOnlyId: observable,
      isAllSelected: computed,
      singleFolderToServerFormat: computed,
      selectedDocumentIds: computed,
      selectedDocumentsCount: computed,

      setSelectAll: action.bound,
      toggleSelectAll: action.bound,
      toggleNode: action.bound,
      clear: action.bound,
      setMark: action.bound,
      selectOnly: action.bound,
      selectOnlyOrClear: action.bound,
      selectDocumentsOnly: action.bound,
    });

    if (isSelectAll) {
      this.setSelectAll(true);
    }
  }

  private _getNodeId(node: TreeStore): string {
    return String(node.data.id);
  }

  private _getClosestMark(node: TreeStore): Mark | undefined {
    let current: TreeStore | undefined = node;

    while (current) {
      const mark = this.marks.get(this._getNodeId(current));

      if (mark) {
        return mark;
      }

      current = current.parent;
    }

    return undefined;
  }

  isChecked(node?: TreeStore): boolean {
    if (!node) {
      return false;
    }

    if (node.data.type === 'file' && !(node.data as TreeNodeFile).contentAvailable) {
      return false;
    }

    if (this._singleOnlyId) {
      return this._getNodeId(node) === this._singleOnlyId;
    }

    return this._getClosestMark(node) === 'include';
  }

  getNodeState(node?: TreeStore): NodeCheckboxState {
    if (!node) {
      return {
        checked: false,
        indeterminate: false,
      };
    }

    if (this._singleOnlyId) {
      return {
        checked: this._getNodeId(node) === this._singleOnlyId,
        indeterminate: false,
      };
    }

    if (node.data.type !== 'folder' || !node.children?.length) {
      const checked = this.isChecked(node);

      return {
        checked,
        indeterminate: false,
      };
    }

    const childrenStates = node.children.map((child) => this.getNodeState(child));
    const hasChecked = childrenStates.some((state) => state.checked || state.indeterminate);
    const hasUnchecked = childrenStates.some((state) => !state.checked || state.indeterminate);

    return {
      checked: hasChecked && !hasUnchecked,
      indeterminate: !!(hasChecked && hasUnchecked),
    };
  }

  get isAllSelected(): boolean {
    return this.marks.get(TREE_ROOT_NODE_ID) === 'include';
  }

  get singleFolderToServerFormat() {
    return this.toServerFormat().folders[0];
  }

  get selectedDocumentIds(): string[] | null {
    const { documents, folders } = this.toServerFormat();

    if (folders.includes(TREE_ROOT_NODE_ID)) {
      return null;
    }

    const selectedDocuments = new Set<string>(documents);

    for (const folderId of folders) {
      if (!folderId) {
        continue;
      }

      const folderStore = this._findTreeStoreById(this.treeStore, folderId);

      if (!folderStore) {
        continue;
      }

      this._collectDocumentIds(folderStore, selectedDocuments);
    }

    return Array.from(selectedDocuments);
  }

  get selectedDocumentsCount(): number {
    const selectedDocumentIds = this.selectedDocumentIds;

    if (!selectedDocumentIds) {
      return Number.POSITIVE_INFINITY;
    }

    return selectedDocumentIds.length;
  }

  clear(): void {
    this.marks.clear();
    this._singleOnlyId = null;
  }

  setSelectAll(selectAll: boolean): void {
    this._singleOnlyId = null;

    if (selectAll) {
      this.marks.clear();
      this.marks.set(TREE_ROOT_NODE_ID, 'include');
    } else {
      this.marks.delete(TREE_ROOT_NODE_ID);
    }
  }

  toggleSelectAll(): void {
    this.setSelectAll(!this.isAllSelected);
  }

  private _forEachFileChild(node: TreeStore, cb: (child: TreeStore) => void): void {
    for (const child of node.children) {
      if (child.data.type === 'file') {
        cb(child);
      }
    }
  }

  private _applyMarkToVirtualFolder(node: TreeStore, mark: Mark): void {
    this._forEachFileChild(node, (child) => {
      if (child.data.type === 'file' && !(child.data as TreeNodeFile).contentAvailable) {
        return;
      }

      this.marks.set(this._getNodeId(child), mark);
    });
    this.marks.delete(this._getNodeId(node));
  }

  private _clearMarksInSubtree(node: TreeStore): void {
    const stack = [...node.children];

    while (stack.length) {
      const current = stack.pop()!;

      this.marks.delete(this._getNodeId(current));
      if (current.children?.length) {
        stack.push(...current.children);
      }
    }
  }

  setMark(node: TreeStore, mark: Mark): void {
    this._singleOnlyId = null;

    const nodeId = this._getNodeId(node);

    if (node.isVirtualFolder) {
      this._applyMarkToVirtualFolder(node, mark);

      return;
    }

    this.marks.set(nodeId, mark);
    if (mark === 'include') {
      this._ensureFolderLoaded(node);
    }
    this._clearMarksInSubtree(node);
  }

  toggleNode(node: TreeStore): void {
    if (node.data.type === 'file' && !(node.data as TreeNodeFile).contentAvailable) {
      return;
    }

    const state = this.getNodeState(node);
    const nextMark: Mark = state.checked ? 'exclude' : 'include';

    this.setMark(node, nextMark);
  }

  selectOnly(node: TreeStore): void {
    if (node.data.type === 'file' && !(node.data as TreeNodeFile).contentAvailable) {
      return;
    }

    this.marks.clear();
    this._singleOnlyId = this._getNodeId(node);
    this.marks.set(this._singleOnlyId, 'include');
  }

  selectOnlyOrClear(node: TreeStore): void {
    const nodeId = this._getNodeId(node);

    if (this._singleOnlyId && this._singleOnlyId === nodeId) {
      this.clear();

      return;
    }

    this.selectOnly(node);
  }

  selectDocumentsOnly(documentIds: Array<string | number>): void {
    const uniqueIds = Array.from(new Set(documentIds.map((id) => String(id))));

    this.marks.clear();
    this._singleOnlyId = null;

    if (uniqueIds.length === 0) {
      return;
    }

    if (uniqueIds.length === 1) {
      const [onlyId] = uniqueIds;

      this._singleOnlyId = onlyId;
      this.marks.set(onlyId, 'include');

      return;
    }

    uniqueIds.forEach((id) => {
      this.marks.set(id, 'include');
    });
  }

  toServerFormat(): ServerSelectionContext {
    if (this.isAllSelected && !this._hasExclusions()) {
      return {
        documents: [],
        folders: [],
      };
    }

    const documents = new Set<string>();
    const folders = new Set<string>();

    this._collectSelections(this.treeStore, documents, folders);

    return {
      documents: Array.from(documents),
      folders: Array.from(folders),
    };
  }

  private _hasExclusions(): boolean {
    for (const mark of this.marks.values()) {
      if (mark === 'exclude') {
        return true;
      }
    }

    return false;
  }

  private _isFile(node: TreeStore): boolean {
    return node.data.type === 'file';
  }

  private _isRoot(nodeId: string): boolean {
    return nodeId === TREE_ROOT_NODE_ID;
  }

  private _addIfCheckedFile(node: TreeStore, nodeId: string, documents: Set<string>): boolean {
    if (!this._isFile(node)) {
      return false;
    }

    if (this.isChecked(node)) {
      documents.add(nodeId);
    }

    return true;
  }

  private _collectFromVirtualFolder(node: TreeStore, documents: Set<string>): boolean {
    if (!node.isVirtualFolder) {
      return false;
    }

    for (const child of node.children) {
      if (child.data.type === 'file' && this.isChecked(child)) {
        documents.add(this._getNodeId(child));
      }
    }

    return true;
  }

  private _shouldAddFolder(node: TreeStore, mark?: Mark): boolean {
    if (mark === 'include') {
      return true;
    }

    if (node.data.type !== 'folder') {
      return false;
    }

    const state = this.getNodeState(node);

    return state.checked && !state.indeterminate;
  }

  private _collectChildren(node: TreeStore, documents: Set<string>, folders: Set<string>): void {
    for (const child of node.children) {
      this._collectSelections(child, documents, folders);
    }
  }

  private _collectSelections(node: TreeStore, documents: Set<string>, folders: Set<string>): void {
    const nodeId = this._getNodeId(node);

    if (this._addIfCheckedFile(node, nodeId, documents)) {
      return;
    }

    if (this._collectFromVirtualFolder(node, documents)) {
      return;
    }

    const mark = this.marks.get(nodeId);

    if (this._isRoot(nodeId)) {
      this._collectChildren(node, documents, folders);

      return;
    }

    if (this._shouldAddFolder(node, mark)) {
      folders.add(nodeId);

      return;
    }

    this._collectChildren(node, documents, folders);
  }

  destroy(): void {
    this.marks.clear();
  }

  private _findTreeStoreById(store: TreeStore | undefined, targetId: string): TreeStore | null {
    if (!store) {
      return null;
    }

    if (String(store.data.id) === String(targetId)) {
      return store;
    }

    for (const child of store.children) {
      const found = this._findTreeStoreById(child, targetId);

      if (found) {
        return found;
      }
    }

    return null;
  }

  private _collectDocumentIds(store: TreeStore, collection: Set<string>): void {
    if (store.data.type === 'file') {
      if ((store.data as TreeNodeFile).contentAvailable) {
        collection.add(this._getNodeId(store));
      }

      return;
    }

    if (!store.children.length) {
      return;
    }

    for (const child of store.children) {
      this._collectDocumentIds(child, collection);
    }
  }

  private _ensureFolderLoaded(node: TreeStore): void {
    if (node.data.type !== 'folder' || node.isVirtualFolder) {
      return;
    }

    if (!node.data.hasChildren || node.children.length > 0) {
      return;
    }

    void node.fetchTree();
  }
}
